import React, { Suspense, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { CommentWithReplyField } from '@contently/toolkit';
import { useFetcher } from 'rest-hooks';
import useAvatarBadge from '../../hooks/useAvatarBadge';
import authorOwnerCheck from '../../utils/authorOwnerCheck';
import useUserContext from '../../hooks/useUserContext';
import CommentResource from '../../resources/CommentResource';
import ReplyChainContainer from './ReplyChainContainer';
import { getReplyCommentParams } from './utils';
import LoadingSpinner from '../../../utils/shared/LoadingSpinner';
import {
  updateResolvedAnnotation,
  updateAnnotationListCacheBust
} from '../../store/textEditorReducer';
import { ACTIVATE_ANNOTATION_EVENT } from '../../editor-ui';

export default function CommentWrapper({ storyId, handleCancel, active, comment, users }) {
  const {
    id: commentId,
    authorable,
    authorIsEmployee,
    authorIsManagingEditor,
    resolved,
    highlightedText
  } = comment;
  const [updatedAt, setUpdatedAt] = useState(comment.updatedAt)
  const [resolveClicked, toggleResolveClicked] = useState(false);
  const { permissions } = useUserContext();
  const editComment = useFetcher(CommentResource.updateShape());
  const createComment = useFetcher(CommentResource.createShape());
  const dispatch = useDispatch();

  const handleEdit = (id, data) => {
    editComment({ id }, { body: data.edit })
      .then(({ updatedAt }) => {
        setUpdatedAt(updatedAt);
      });
  };

  const handleResolve = (e, id) => {
    if (e) e.preventDefault();
    if (comment.uuid) dispatch(updateResolvedAnnotation(comment.uuid));
    editComment({ id }, { resolved: 1 });
    dispatch(updateAnnotationListCacheBust(true));
    toggleResolveClicked(true);
  };

  const handleUnresolve = (e, id) => {
    if (e) e.preventDefault();
    if (comment.uuid) dispatch(updateResolvedAnnotation(comment.uuid));
    editComment({ id }, { resolved: 0 });
    dispatch(updateAnnotationListCacheBust(true));
    toggleResolveClicked(false);
  };

  const handleReply = (data, id) => {
    createComment({}, getReplyCommentParams(data, storyId, id), [
      [
        CommentResource.listShape(),
        { parent_id: id },
        (newCommentId, commentIds) => [...(commentIds || []), newCommentId]
      ]
    ]);
  };

  const [avatarTooltip, avatarBadge, avatarBadgeColor] = useAvatarBadge(
    authorable.fullName,
    authorIsEmployee,
    authorIsManagingEditor
  );

  const mark = document.querySelector(`#js-view-story-container [uuid="${comment.uuid}"]`);
  const annotationInStoryText = Boolean(mark);

  const handleClick = () => {
    if (annotationInStoryText) {
      const coordsRaw = mark.getBoundingClientRect();
      const coords = { top: coordsRaw.top, bottom: coordsRaw.bottom, left: coordsRaw.left, right: coordsRaw.right };
      const payload = { annotationUUID: comment.uuid, coords };
      const event = new CustomEvent(ACTIVATE_ANNOTATION_EVENT, { detail: payload });
      document.dispatchEvent(event);
    };
  };

  const showReplyForm = () => {
    if (active && !resolved && !resolveClicked && mark) {
      return false;
    } else if (active && !resolved && !resolveClicked) {
      return true;
    }
    return false;
  };

  const isAnnotationAndPresentInStory = () => {
    if (comment.uuid) return annotationInStoryText;

    return comment.isAnnotation;
  }

  return (
    <CommentWithReplyField
      {...comment}
      updatedAt={updatedAt}
      fullName={authorable.fullName}
      avatar={authorable.avatar}
      authorIsOwner={authorOwnerCheck(authorable.id, permissions.id)}
      authorTooltip={avatarTooltip}
      authorBadge={avatarBadge}
      authorBadgeColor={avatarBadgeColor}
      resolved={resolved}
      highlightedText={highlightedText}
      onResolve={e => handleResolve(e, comment.id)}
      onUnresolve={e => handleUnresolve(e, comment.id)}
      allowUnresolve
      onReplyCancel={e => handleCancel(e)}
      onEditCancel={e => handleCancel(e)}
      onReplySubmit={e => handleReply(e, comment.id)}
      onEditSubmit={data => {
        handleEdit(commentId, data);
      }}
      width="100%"
      showReplyForm={showReplyForm()}
      borderBottom={false}
      borderSides={false}
      users={users}
      borderLeft={isAnnotationAndPresentInStory()}
      inStoryText={annotationInStoryText}
      handleClick={handleClick}
    >
      <Suspense fallback={<LoadingSpinner />}>
        <ReplyChainContainer commentId={comment.id} showAllComments={active} sidebar />
      </Suspense>
    </CommentWithReplyField>
  );
}

CommentWrapper.propTypes = {
  storyId: PropTypes.number.isRequired,
  handleCancel: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  comment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    authorable: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fullName: PropTypes.string,
      avatar: PropTypes.string
    }).isRequired,
    resolved: PropTypes.number.isRequired,
    authorIsEmployee: PropTypes.bool,
    authorIsManagingEditor: PropTypes.bool,
    uuid: PropTypes.string,
    highlightedText: PropTypes.string
  }).isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      display: PropTypes.string.isRequired,
      avatar: PropTypes.string.isRequired
    })
  ).isRequired
};



// WEBPACK FOOTER //
// ./app/javascript/client/components/textEditor/CommentWrapper.jsx